// Locale-aware numeric entry helpers.
//
// Turkish farmers may be on a device set to any locale: some keyboards emit a
// comma for the decimal separator (tr, de, fr…), others a dot (en, …). We must
// accept whatever the user's browser / mobile setting produces, and display
// numbers the way that user expects to read them.
//
// - `DECIMAL_SEP` / `GROUP_SEP` are detected from the runtime's default locale
//   (i.e. the browser / OS setting), NOT forced to tr-TR.
// - `parseFlexibleNumber` accepts BOTH comma and dot and returns a plain
//   machine string ("1234.56") for form state, resolving the thousands/decimal
//   ambiguity using the detected locale.
// - `formatNumber` renders using the detected locale.

const _probe = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).formatToParts(11111.11);

export const DECIMAL_SEP = (_probe.find(p => p.type === 'decimal') || { value: ',' }).value;
export const GROUP_SEP = (_probe.find(p => p.type === 'group') || { value: '.' }).value;

// "1234.56" -> "1.234,56" (or "1,234.56" on a dot-locale device)
export function formatNumber(n, { min = 2, max = 2 } = {}) {
  if (n === '' || n === null || n === undefined || isNaN(Number(n))) return '';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(Number(n));
}

/**
 * Parse a human-typed number that may use a comma OR a dot for decimals and
 * either for thousands grouping. Returns a machine-parseable string (dot
 * decimal, no grouping) suitable for form state / Number(). Returns '' when
 * there is no numeric content.
 *
 * Rules:
 *  - Both separators present -> the LAST one is the decimal; the other groups.
 *  - One separator, appearing more than once -> grouping (e.g. 1.234.567).
 *  - One separator, appearing once, followed by exactly 3 digits -> ambiguous;
 *    the detected locale decides (matches that user's own convention).
 *  - One separator, appearing once, followed by 1/2/4+ digits -> decimal
 *    (grouping is always exactly 3, so this can only be a decimal).
 */
export function parseFlexibleNumber(input) {
  if (input === null || input === undefined) return '';
  let s = String(input).trim().replace(/[^\d.,-]/g, '');
  if (s === '' || s === '-') return '';

  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  if (s === '') return '';

  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  let out;

  if (lastComma !== -1 && lastDot !== -1) {
    const decSep = lastComma > lastDot ? ',' : '.';
    const grpSep = decSep === ',' ? '.' : ',';
    out = s.split(grpSep).join('').replace(decSep, '.');
  } else if (lastComma !== -1 || lastDot !== -1) {
    const sep = lastComma !== -1 ? ',' : '.';
    const parts = s.split(sep);
    const decimalsLen = parts[parts.length - 1].length;
    if (parts.length > 2) {
      // multiple same separators -> all grouping (1.234.567)
      out = parts.join('');
    } else if (decimalsLen === 3) {
      // ambiguous "1.500" / "1,500" -> the user's locale decides
      out = sep === DECIMAL_SEP ? parts.join('.') : parts.join('');
    } else {
      // 1, 2 or 4+ trailing digits -> decimal
      out = parts.join('.');
    }
  } else {
    out = s;
  }

  if (out === '' || out === '.') return '';
  return neg ? `-${out}` : out;
}
